import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Tooltip,
  Alert,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { fetchStudentCourseProfile, signOffStudent, downloadCertificatePdf } from "../../api/apiClient";
import { useTheme } from "@mui/material/styles";
import { useTenant } from "../../contexts/TenantContext";
import { readSignOffError } from "../../components/roster/bulkSignOff";
import StudentCertificatesStatus from "../../components/certificates/StudentCertificatesStatus";

const DS = {
  surface: "var(--color-surface-2)",
  border: "rgba(0,0,0,0.08)",
  borderStrong: "rgba(0,0,0,0.13)",
};

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

function StatusIcon({ value }) {
  if (value === true) return <CheckCircleIcon sx={{ fontSize: 17, color: "var(--color-success)" }} />;
  if (value === false) return <CancelIcon sx={{ fontSize: 17, color: "var(--color-danger)" }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 17, color: "var(--color-ink-4)" }} />;
}

function ResultChip({ result }) {
  if (!result) return (
    <Chip label="Not Evaluated" size="small"
      sx={{ fontSize: "0.7rem", bgcolor: "var(--color-surface-2)", color: "var(--color-ink-3)" }} />
  );
  const color = result === "PASS"
    ? { bgcolor: "var(--color-success-bg)", color: "#15803d" }
    : result === "FAIL"
    ? { bgcolor: "var(--color-danger-bg)", color: "var(--color-danger)" }
    : { bgcolor: "var(--color-warn-bg)", color: "#854d0e" };
  return <Chip label={result} size="small" sx={{ fontSize: "0.7rem", fontWeight: 700, ...color }} />;
}

function SectionHeader({ title }) {
  return (
    <Typography variant="caption" sx={{
      display: "block", fontWeight: 700, fontSize: "0.68rem",
      textTransform: "uppercase", letterSpacing: "0.07em",
      color: "var(--color-ink-4)", mb: 1.25, mt: 0.5,
    }}>
      {title}
    </Typography>
  );
}

function formatSessionDate(dateStr) {
  if (!dateStr) return "Date TBD";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
}

export default function StudentProfileDrawer({
  open, courseId, studentId, studentName, onClose, onSignOff,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);
  // Requirements the server refused the sign-off for (409), and the coordinator/admin "sign off anyway" form.
  const [serverMissing, setServerMissing] = useState([]);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const canOverride = ((useTenant()?.user?.roles) || []).some((r) => ["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"].includes(r));

  // Success-phase state
  const [phase, setPhase] = useState("idle"); // "idle" | "success"
  const [certId, setCertId] = useState(null);
  const [certStudentName, setCertStudentName] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const load = useCallback(() => {
    if (!open || !courseId || !studentId) return;
    setLoading(true);
    setError(null);
    fetchStudentCourseProfile(courseId, studentId)
      .then(setProfile)
      .catch(() => setError("Failed to load student profile."))
      .finally(() => setLoading(false));
  }, [open, courseId, studentId]);

  useEffect(() => { load(); }, [load]);

  // Restore success phase when profile shows an existing sign-off/certificate
  useEffect(() => {
    if (profile?.passed && profile?.certificateId && phase === "idle") {
      setCertId(profile.certificateId);
      setCertStudentName(profile.fullName || studentName);
      setPhase("success");
    }
  }, [profile, phase, studentName]);

  // Clean up blob URL and reset success state when drawer closes
  useEffect(() => {
    if (!open) {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPhase("idle");
      setCertId(null);
      setCertStudentName(null);
      setPreviewOpen(false);
      setPdfBlobUrl(null);
      setPreviewError(null);
    }
  // eslint-disable-next-line
  }, [open]);

  async function handleSignOff(passed, reason) {
    setSigning(true);
    setSignError(null);
    setServerMissing([]);
    try {
      const res = reason
        ? await signOffStudent(courseId, studentId, passed, reason)
        : await signOffStudent(courseId, studentId, passed);
      setOverrideOpen(false);
      setOverrideReason("");
      if (passed && res?.certificateId) {
        setCertId(res.certificateId);
        setCertStudentName(profile?.fullName || studentName);
        setPhase("success");
      } else {
        await load();
      }
      if (onSignOff) onSignOff();
    } catch (err) {
      const e = readSignOffError(err);
      if (e.requirementsNotMet) {
        setServerMissing(e.missing);
        setSignError("This student has not met the class requirements.");
      } else {
        setSignError(e.message === "Sign-off failed" ? "Sign-off failed. Please try again." : e.message);
      }
    } finally {
      setSigning(false);
    }
  }

  async function handlePreviewToggle() {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    if (pdfBlobUrl) {
      setPreviewOpen(true);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const blob = await downloadCertificatePdf(certId);
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setPreviewOpen(true);
    } catch {
      setPreviewError("Preview failed to load.");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleDownload() {
    try {
      const blob = pdfBlobUrl
        ? await fetch(pdfBlobUrl).then((r) => r.blob())
        : await downloadCertificatePdf(certId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certificate - ${certStudentName || "Student"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // download failed silently
    }
  }

  const sessionsAttended = profile ? profile.sessions.filter((s) => s.attended).length : 0;
  const testsPassed = profile ? profile.tests.filter((t) => t.passed === true).length : 0;
  const requiredChecklists = profile ? profile.checklists.filter((c) => c.requiredForPass) : [];
  const checklistsPassed = requiredChecklists.filter((c) => c.finalResult === "PASS").length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 3, pt: 2.5, pb: 2,
        borderBottom: `1px solid ${DS.border}`,
        flexShrink: 0, bgcolor: "#fff",
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Avatar sx={{
            width: 48, height: 48, bgcolor: tPrimary,
            fontWeight: 700, fontSize: "1rem", flexShrink: 0,
          }}>
            {initials(profile?.fullName || studentName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="h6" fontWeight={800} color={tPrimary} sx={{ lineHeight: 1.2, fontFamily: "var(--font-display)" }}>
                {profile?.fullName || studentName || "Loading…"}
              </Typography>
              {profile?.passed && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                  label="Signed Off"
                  size="small"
                  sx={{ bgcolor: "var(--color-success-bg)", color: "#15803d", fontWeight: 700, fontSize: "0.7rem" }}
                />
              )}
            </Box>
            {profile?.email && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
                {profile.email}
              </Typography>
            )}
            {profile?.signedOffByName && profile?.signedOffAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "block" }}>
                Signed off by {profile.signedOffByName} ·{" "}
                {new Date(profile.signedOffAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0, mt: "-4px" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Summary chips */}
        {profile && (
          <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
            <Chip
              icon={<StatusIcon value={profile.allSessionsAttended} />}
              label={`${sessionsAttended}/${profile.sessions.length} Sessions`}
              size="small" variant="outlined"
              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
            />
            <Chip
              icon={<StatusIcon value={profile.tests.length > 0 ? profile.allTestsPassed : null} />}
              label={`${testsPassed}/${profile.tests.length} Tests`}
              size="small" variant="outlined"
              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
            />
            <Chip
              icon={<StatusIcon value={requiredChecklists.length > 0 ? profile.allRequiredChecklistsPassed : null} />}
              label={`${checklistsPassed}/${requiredChecklists.length} Skills (req'd)`}
              size="small" variant="outlined"
              sx={{ fontSize: "0.75rem", fontWeight: 600 }}
            />
          </Box>
        )}
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5, bgcolor: DS.surface }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}

        {profile && !loading && (
          <>
            {/* Sessions */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: `1px solid ${DS.border}`, p: 2, mb: 2 }}>
              <SectionHeader title="Sessions" />
              {profile.sessions.length === 0 && (
                <Typography variant="body2" color="text.secondary">No sessions scheduled.</Typography>
              )}
              {profile.sessions.map((s, i) => (
                <Box key={s.sessionId}>
                  {i > 0 && <Divider sx={{ my: 1.25, borderColor: DS.border }} />}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <StatusIcon value={s.attended} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} color="var(--color-ink)">
                        Session {s.sessionNumber}
                        {s.location && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            · {s.location}
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatSessionDate(s.sessionDate)}
                        {s.checkedInAt && (
                          <> · Checked in {new Date(s.checkedInAt).toLocaleTimeString("en-US", {
                            hour: "numeric", minute: "2-digit",
                          })}</>
                        )}
                      </Typography>
                    </Box>
                    <Chip
                      label={s.attended ? "Attended" : "Absent"}
                      size="small"
                      sx={{
                        fontSize: "0.68rem", fontWeight: 700,
                        bgcolor: s.attended ? "var(--color-success-bg)" : "var(--color-danger-bg)",
                        color: s.attended ? "#15803d" : "var(--color-danger)",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Tests */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: `1px solid ${DS.border}`, p: 2, mb: 2 }}>
              <SectionHeader title="Written Tests" />
              {profile.tests.length === 0 && (
                <Typography variant="body2" color="text.secondary">No tests assigned.</Typography>
              )}
              {profile.tests.map((t, i) => (
                <Box key={t.testId}>
                  {i > 0 && <Divider sx={{ my: 1.25, borderColor: DS.border }} />}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={{ mt: "2px" }}><StatusIcon value={t.passed} /></Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} color="var(--color-ink)" noWrap>
                        {t.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.score != null
                          ? `${t.score}/${t.totalPoints} pts (${t.percentage}%) · Passing: ${t.passingScore} pts`
                          : "Not attempted"}
                        {t.attemptCount > 0 && ` · ${t.attemptCount}/${t.maxRetakes + 1} attempt${t.attemptCount !== 1 ? "s" : ""}`}
                        {t.submittedAt && <> · {new Date(t.submittedAt).toLocaleDateString()}</>}
                      </Typography>
                    </Box>
                    <ResultChip result={t.passed === true ? "PASS" : t.passed === false ? "FAIL" : null} />
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Skill Checklists */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: `1px solid ${DS.border}`, p: 2, mb: 2 }}>
              <SectionHeader title="Skill Check Sheets" />
              {profile.checklists.length === 0 && (
                <Typography variant="body2" color="text.secondary">No skill sheets assigned.</Typography>
              )}
              {profile.checklists.map((c, i) => (
                <Box key={c.checklistId}>
                  {i > 0 && <Divider sx={{ my: 1.25, borderColor: DS.border }} />}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={{ mt: "2px" }}>
                      <StatusIcon
                        value={
                          c.finalResult === "PASS" ? true
                            : c.finalResult === "FAIL" ? false
                            : null
                        }
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography variant="body2" fontWeight={600} color="var(--color-ink)" noWrap>
                          {c.title}
                        </Typography>
                        {c.requiredForPass && (
                          <Chip label="Required" size="small" sx={{
                            fontSize: "0.62rem", fontWeight: 700,
                            bgcolor: "var(--color-primary-10)", color: "var(--color-primary)", height: 18,
                          }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {c.gradePercent != null ? `${Math.round(c.gradePercent)}%` : "Not evaluated"}
                        {c.evaluatorName && ` · Evaluated by ${c.evaluatorName}`}
                        {c.evaluatedAt && <> · {new Date(c.evaluatedAt).toLocaleDateString()}</>}
                      </Typography>
                    </Box>
                    <ResultChip result={c.finalResult} />
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Sticky sign-off footer */}
      {profile && (
        <Box sx={{
          borderTop: `1px solid ${DS.borderStrong}`,
          bgcolor: "#fff", flexShrink: 0,
        }}>

          {/* ── Success state after sign-off ── */}
          {phase === "success" ? (
            <Box>
              {/* Signed-off confirmation banner */}
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1.25,
                px: 3, py: 1.75,
                bgcolor: "var(--color-success-bg)", borderBottom: "1px solid var(--color-success-bg)",
              }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: "var(--color-success)", flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#15803d">
                    Signed Off · {(profile?.signedOffAt
                      ? new Date(profile.signedOffAt)
                      : new Date()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </Typography>
                  <Typography variant="caption" color="#166534">
                    Certificate generated for {certStudentName}
                  </Typography>
                </Box>
              </Box>

              {/* PDF document row */}
              <Box sx={{ px: 3, pt: 1.75, pb: previewOpen ? 0 : 1.75 }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.25, borderRadius: "8px",
                  border: "1px solid var(--color-hairline-cloud)", bgcolor: "var(--color-surface-2)",
                }}>
                  <DescriptionIcon sx={{ fontSize: 22, color: "var(--color-ink-3)", flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} color="var(--color-ink)" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    Certificate – {certStudentName}.pdf
                  </Typography>
                  <Button
                    size="small"
                    endIcon={previewLoading
                      ? <CircularProgress size={12} />
                      : previewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={handlePreviewToggle}
                    disabled={previewLoading}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.75rem", color: tPrimary, flexShrink: 0 }}
                  >
                    Preview
                  </Button>
                </Box>

                {/* Expandable PDF iframe */}
                {previewOpen && pdfBlobUrl && (
                  <Box sx={{ mt: 1.5, borderRadius: "8px", overflow: "hidden", border: "1px solid var(--color-hairline-cloud)" }}>
                    <iframe
                      src={pdfBlobUrl}
                      title="Certificate preview"
                      style={{ width: "100%", height: 380, border: "none", display: "block" }}
                    />
                  </Box>
                )}
                {previewError && (
                  <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>{previewError}</Alert>
                )}
              </Box>

              {/* Download + Close */}
              <Box sx={{ display: "flex", gap: 1.5, px: 3, pt: 1.5, pb: 2 }}>
                <Button
                  variant="outlined" fullWidth startIcon={<FileDownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    fontWeight: 700, textTransform: "none", borderRadius: "8px",
                    borderColor: tPrimary, color: tPrimary,
                  }}
                >
                  Download PDF
                </Button>
                <Button
                  variant="contained" fullWidth onClick={onClose}
                  sx={{
                    bgcolor: tPrimary, "&:hover": { bgcolor: "#002d56" },
                    fontWeight: 700, textTransform: "none", borderRadius: "8px",
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>

          ) : (
            /* ── Normal sign-off footer ── */
            <Box sx={{ px: 3, py: 2 }}>
              {signError && (
                <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
                  {signError}
                  {serverMissing.length > 0 && (
                    <Box component="ul" data-testid="sign-off-missing" sx={{ m: 0, pl: 2.5 }}>
                      {serverMissing.map((m, i) => <li key={`${m.code}-${i}`}>{m.label}</li>)}
                    </Box>
                  )}
                </Alert>
              )}

              {profile.eligibleForSignOff && !profile.passed && (
                <Box>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1, mb: 1.5,
                    p: 1.25, bgcolor: "var(--color-success-bg)", borderRadius: "8px", border: "1px solid var(--color-success-bg)",
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: "var(--color-success)" }} />
                    <Typography variant="caption" fontWeight={600} color="#15803d">
                      All requirements met — eligible for sign-off
                    </Typography>
                  </Box>
                  <Button
                    variant="contained" fullWidth disabled={signing}
                    onClick={() => handleSignOff(true)}
                    sx={{
                      bgcolor: "#15803d", "&:hover": { bgcolor: "#166534" },
                      fontWeight: 700, textTransform: "none", borderRadius: "8px",
                    }}
                  >
                    {signing ? "Signing Off…" : "Sign Off Student"}
                  </Button>
                </Box>
              )}

              {!profile.eligibleForSignOff && !profile.passed && (
                <Box>
                  <Box sx={{
                    display: "flex", alignItems: "flex-start", gap: 1, p: 1.25,
                    bgcolor: "var(--color-warn-bg)", borderRadius: "8px", border: "1px solid var(--color-warn-bg)",
                  }}>
                    <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "var(--color-warn)", mt: "2px" }} />
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="var(--color-warn)" component="div">
                        Requirements not yet complete
                      </Typography>
                      {(profile.missingRequirements || []).map((m, i) => (
                        <Typography key={`${m.code}-${i}`} variant="caption" color="var(--color-warn)" component="div">
                          {m.label}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  {canOverride && !overrideOpen && (
                    <Button fullWidth size="small" sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                      onClick={() => setOverrideOpen(true)}>
                      Sign off anyway
                    </Button>
                  )}
                  {canOverride && overrideOpen && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        size="small" fullWidth multiline minRows={2} label="Reason for signing off anyway (required)"
                        value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                      />
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <Button size="small" onClick={() => { setOverrideOpen(false); setOverrideReason(""); }}
                          sx={{ textTransform: "none" }}>Cancel</Button>
                        <Button size="small" variant="contained" fullWidth
                          disabled={signing || !overrideReason.trim()}
                          onClick={() => handleSignOff(true, overrideReason.trim())}
                          sx={{ textTransform: "none", fontWeight: 700 }}>
                          {signing ? "Signing Off…" : "Confirm sign-off anyway"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {profile.passed && (
                <Box>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1, mb: 1.5,
                    p: 1.25, bgcolor: "var(--color-success-bg)", borderRadius: "8px", border: "1px solid var(--color-success-bg)",
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: "var(--color-success)" }} />
                    <Typography variant="caption" fontWeight={600} color="#15803d">
                      Student signed off
                      {profile.signedOffByName ? ` by ${profile.signedOffByName}` : ""}
                      {profile.signedOffAt ? ` on ${new Date(profile.signedOffAt).toLocaleDateString()}` : ""}
                    </Typography>
                  </Box>
                  {!profile.certificateId && (
                    <StudentCertificatesStatus classId={courseId} userId={studentId} refreshKey={profile.signedOffAt}
                      fallback={
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}
                          data-testid="certificates-sent-later">
                          Their certificates go out when the class is complete and you press Send certificates on the roster.
                        </Typography>
                      } />
                  )}
                  <Tooltip title="This will clear the sign-off and mark the student as not passed">
                    <Button
                      variant="outlined" fullWidth color="error" disabled={signing}
                      onClick={() => handleSignOff(false)}
                      sx={{ fontWeight: 700, textTransform: "none", borderRadius: "8px" }}
                    >
                      {signing ? "Revoking…" : "Revoke Sign-Off"}
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
}
